// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useMemo } from 'react';
import { sanitizeUrl } from '@braintree/sanitize-url';

import { standardFormatShort } from 'constants/dateFormats';
import { useSitecoreContext } from './useSitecoreContext';

const asiaPacificCountryCodes = [
  'AUS',
  'CHL',
  'CHN',
  'HKG',
  'JPN',
  'MYS',
  'NZL',
  'PER',
  'SGP',
  'KOR',
  'TWN',
  'THA',
];

const useHelpers = (rendering?) => {
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor: boolean | undefined = sitecoreContext.pageEditing;
  const anchorId: string = useMemo(() => rendering?.params?.anchorId, [rendering]);
  const backgroundClass: string = useMemo(
    () => rendering?.params?.background || 'background-white',
    [rendering]
  );
  const hasPlaceholders: Array<object> = useMemo(() => {
    return (
      rendering?.placeholders &&
      Object.keys(rendering?.placeholders)
        .map((key) => {
          return rendering?.placeholders[key];
        })
        .flat(Infinity).length
    );
  }, [rendering]);
  const isPlaceholderNotEmpty = (placeholderName: string): boolean => {
    return (
      rendering?.placeholders?.[placeholderName] && rendering.placeholders[placeholderName].length
    );
  };
  const localizeDate = (timestamp: string | undefined, options = standardFormatShort): string => {
    if (!timestamp) return '';
    const isMidNight: boolean = timestamp.toString().includes('T00:00:00Z');
    if (isMidNight) {
      return new Intl.DateTimeFormat(sitecoreContext.language, {
        ...options,
        timeZone: 'UTC',
      }).format(new Date(Date.parse(timestamp)));
    }
    return new Intl.DateTimeFormat(sitecoreContext.language, options).format(
      new Date(Date.parse(timestamp))
    );
  };
  const formatNumber = (
    number: number | string,
    displayType = 'Decimal',
    fractionDigits = 2
  ): number => {
    const isPercentage: boolean =
      number?.toString().includes('%') || displayType.toLowerCase() === 'percentage';

    const { language } = sitecoreContext;
    const countryCode: string =
      sitecoreContext.fundsConfiguration?.countryInvestors.countryCode ||
      sitecoreContext.fundCentreConfiguration?.countryInvestors.countryCode;

    const options: object = {
      minimumFractionDigits: asiaPacificCountryCodes.includes(countryCode) ? 4 : fractionDigits,
      maximumFractionDigits: asiaPacificCountryCodes.includes(countryCode) ? 4 : fractionDigits,
    };

    if (isPercentage) {
      number = parseFloat(number.replace('%', ''));
      options.style = 'percent';
      // Convert number to decimal form for percentage formatting
      number /= 100;
    }

    const formatter = new Intl.NumberFormat(language, options);

    if (isNaN(number)) return number;
    return formatter.format(number);
  };

  const formatCurrency = (
    number: number | string,
    currency = 'GBP',
    notation = 'standard',
    fractionDigits = 2
  ): number => {
    const isPercentage: boolean | undefined = number?.toString().includes('%');
    if (typeof number === 'string') {
      // Remove the percentage sign and convert to a number
      number = parseFloat(number.replace('%', ''));
    }
    if (isNaN(number) || !currency) return '';

    const { language } = sitecoreContext;
    const countryCode: string =
      sitecoreContext.fundsConfiguration?.countryInvestors.countryCode ||
      sitecoreContext.fundCentreConfiguration?.countryInvestors.countryCode;

    const options: object = {
      minimumFractionDigits: asiaPacificCountryCodes.includes(countryCode) ? 4 : fractionDigits,
      maximumFractionDigits: asiaPacificCountryCodes.includes(countryCode) ? 4 : fractionDigits,
      notation: notation,
      currency,
    };

    if (isPercentage) {
      options.style = 'percent';
      // Convert number to decimal form for percentage formatting
      number /= 100;
    }

    const formatter = new Intl.NumberFormat(language, options);

    const formattedPrice: number = formatter.format(number);

    // fix negative zeros
    if (Number(formattedPrice) === 0) {
      return formatter.format(0);
    }

    return formattedPrice;
  };
  const sanitizeURL = (url) => {
    return sanitizeUrl(url);
  };
  const updateUrlParams = (arrayOfParams: UrlParam[] = [], keyToExclude?: string) => {
    if (!isExperienceEditor && typeof window !== 'undefined') {
      const url: URL = new URL(window.location.href);
      const searchParams = new URLSearchParams(url.search);

      if (arrayOfParams.length) {
        arrayOfParams.forEach((param) => {
          if (param.value) {
            searchParams.set(param.name, param.value);
          } else {
            searchParams.delete(param.name);
          }
        });
        url.search = searchParams.toString();
        window.history.replaceState(window.history.state, '', url.href);
      }

      const result = Object.fromEntries(searchParams.entries());

      if (keyToExclude) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [keyToExclude]: _, ...rest } = result;
        return rest;
      }

      return result;
    }

    return {};
  };
  const isFocusWithin = (event): boolean => {
    if (!event.relatedTarget) return true;
    return event.currentTarget.contains(event.relatedTarget);
  };
  const vectorResolver = (virtualVectors: Array<object>, currentPathVector: string): string => {
    const relatedVectorType = virtualVectors.find(
      (vectorType) => vectorType.vectorPath === currentPathVector
    );
    return relatedVectorType ? relatedVectorType.siteName : '';
  };

  const getErrorDictionary = (error) => {
    const status = error?.statusCode;
    if (status !== undefined) return `error${status}`;
    return 'errorUnknown';
  };

  const sitecoreLanguage = useMemo(() => sitecoreContext?.language, [sitecoreContext?.language]);
  const language = useMemo(() => sitecoreContext?.language, [sitecoreContext?.language]);

  return {
    anchorId,
    backgroundClass,
    hasPlaceholders,
    isExperienceEditor,
    isPlaceholderNotEmpty,
    localizeDate,
    formatNumber,
    formatCurrency,
    sanitizeURL,
    updateUrlParams,
    isFocusWithin,
    vectorResolver,
    sitecoreLanguage,
    language,
    getErrorDictionary,
  };
};
export default useHelpers;
